<template>
  <woot-modal :show="show" :on-close="onClose">
    <div class="flex flex-col modal-container">
      <woot-modal-header
        :header-title="$t('CRM_CONFIGS.EDIT_GUEST.TITLE')"
        :header-content="$t('CRM_CONFIGS.EDIT_GUEST.DESC')"
      />

      <div class="px-8 pb-8 pt-4 overflow-y-auto max-h-[85vh]">
        <div v-if="isFetching" class="flex items-center justify-center py-20">
          <spinner />
        </div>

        <form v-else @submit.prevent="submitUpdateGuest">
          <div class="grid grid-cols-2 gap-x-6 gap-y-1">
            <!-- Basic Info -->
            <woot-input
              v-model.trim="guestForm.hoTen"
              :label="$t('CRM_CONFIGS.CREATE_GUEST.FORM.NAME_LABEL')"
              :placeholder="
                $t('CRM_CONFIGS.CREATE_GUEST.FORM.NAME_PLACEHOLDER')
              "
              :error="
                $v.guestForm.hoTen.$error
                  ? $t('CRM_CONFIGS.CREATE_GUEST.FORM.NAME_ERROR')
                  : ''
              "
              class="mb-2"
              @blur="$v.guestForm.hoTen.$touch"
            />

            <woot-input
              v-model.trim="guestForm.dienThoai"
              :label="$t('CRM_CONFIGS.CREATE_GUEST.FORM.PHONE_LABEL')"
              class="mb-2"
              disabled
            />

            <woot-input
              v-model="guestForm.birthday"
              :label="$t('CRM_CONFIGS.CREATE_GUEST.FORM.DOB_LABEL')"
              type="date"
              class="mb-2"
            />

            <div class="mb-2 text-left">
              <label
                class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2"
              >
                {{ $t('CRM_CONFIGS.CREATE_GUEST.FORM.GENDER_LABEL') }}
              </label>
              <select
                v-model="guestForm.gioiTinh"
                class="w-full h-[40px] text-sm bg-slate-50 dark:bg-slate-800/40 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700/80 rounded-lg px-3 outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500 transition-all shadow-sm"
              >
                <option value="">-- Chọn giới tính --</option>
                <option value="Nam">Nam</option>
                <option value="Nữ">Nữ</option>
                <option value="Khác">Khác</option>
              </select>
            </div>

            <woot-input
              v-model.trim="guestForm.quocTich"
              :label="$t('CRM_CONFIGS.CREATE_GUEST.FORM.NATION_LABEL')"
              placeholder="VD: VN"
              class="mb-2"
            />
            <woot-input
              v-model.trim="guestForm.email"
              :label="$t('CRM_CONFIGS.CREATE_GUEST.FORM.EMAIL_LABEL')"
              type="email"
              class="mb-2"
            />

            <woot-input
              v-model.trim="guestForm.cccd"
              label="CCCD/Passport"
              class="mb-2"
            />

            <woot-input
              v-model.trim="guestForm.congTy"
              label="Công ty"
              class="mb-2"
            />

            <div class="col-span-2">
              <woot-input
                v-model.trim="guestForm.diaChi"
                :label="$t('CRM_CONFIGS.CREATE_GUEST.FORM.ADDRESS_LABEL')"
                class="mb-2"
              />
            </div>
          </div>

          <div
            class="flex justify-end gap-3 pt-6 mt-2 border-t border-slate-50 dark:border-slate-800/50"
          >
            <woot-button
              variant="clear"
              color-scheme="secondary"
              @click.prevent="onClose"
            >
              {{ $t('CRM_CONFIGS.CREATE_GUEST.FORM.CANCEL_BTN') }}
            </woot-button>
            <woot-button
              type="submit"
              icon="checkmark"
              :is-loading="isUpdating"
              :disabled="$v.guestForm.$invalid"
            >
              {{ $t('CRM_CONFIGS.EDIT_GUEST.SUBMIT_BTN') }}
            </woot-button>
          </div>
        </form>
      </div>
    </div>
  </woot-modal>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import alertMixin from 'shared/mixins/alertMixin';
import Spinner from 'shared/components/Spinner.vue';

export default {
  components: {
    Spinner,
  },
  mixins: [alertMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    contact: {
      type: Object,
      required: true,
    },
    crmContact: {
      type: Object,
      required: true,
    },
    onClose: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      isFetching: true,
      isUpdating: false,
      guestForm: {
        id: 0,
        maKhach: '',
        hoTen: '',
        dienThoai: '',
        email: '',
        gioiTinh: '',
        birthday: '',
        quocTich: '',
        diaChi: '',
        cccd: '',
        congTy: '',
        danhXung: '',
        zaloId: '',
        fbId: '',
        type: 0,
        maDatPhong: '',
        loaiPhong: '',
        khachSan: '',
        ngayXacNhan: '',
        ngayDen: '',
        ngayDi: '',
        tag: [],
        action: '',
        ngayTao: '',
        isPublic: true,
        ngonNgu: 'VNM',
        owner: '',
        chatContactId: 0,
        avatar: '',
        tenLoai: '',
      },
    };
  },
  validations: {
    guestForm: {
      hoTen: { required },
    },
  },
  mounted() {
    this.fetchGuestDetails();
  },
  methods: {
    async fetchGuestDetails() {
      if (
        !this.crmContact ||
        (!this.crmContact.id && !this.crmContact.maKhach)
      ) {
        this.isFetching = false;
        return;
      }
      this.isFetching = true;
      try {
        const identifier = this.crmContact.maKhach || this.crmContact.id;
        const responseData = await this.$store.dispatch(
          'contacts/getCrmGuest',
          identifier
        );
        if (responseData) {
          // Handle both { data: {...} } and {...} formats
          const guestDetail = responseData.data || responseData;
          // Map response fields to form, handling nulls
          Object.keys(this.guestForm).forEach(key => {
            if (guestDetail[key] !== undefined && guestDetail[key] !== null) {
              this.guestForm[key] = guestDetail[key];
            }
          });

          // Ensure chatContactId is set to contact.id if not present
          if (!this.guestForm.chatContactId) {
            this.guestForm.chatContactId = this.contact.id;
          }

          // Handle date fields formatting for <input type="date">
          ['birthday', 'ngayDen', 'ngayDi'].forEach(dateField => {
            if (
              this.guestForm[dateField] &&
              this.guestForm[dateField].includes('T')
            ) {
              this.guestForm[dateField] =
                this.guestForm[dateField].split('T')[0];
            } else if (
              this.guestForm[dateField] &&
              this.guestForm[dateField].includes(' ')
            ) {
              this.guestForm[dateField] =
                this.guestForm[dateField].split(' ')[0];
            }
          });
        }
      } catch (error) {
        this.showAlert('Không thể tải thông tin chi tiết từ CRM');
      } finally {
        this.isFetching = false;
      }
    },
    async submitUpdateGuest() {
      this.$v.$touch();
      if (this.$v.$invalid) return;

      this.isUpdating = true;
      try {
        const response = await this.$store.dispatch(
          'contacts/updateCrmGuest',
          this.guestForm
        );

        if (
          response &&
          (response.success === '1' ||
            response.success === 1 ||
            response.success === true)
        ) {
          this.showAlert('Cập nhật thông tin CRM thành công');

          // Update the local storage of linked CRM contact in Chatwoot custom_attributes
          const updatedCrmInfo = {
            ...this.crmContact,
            hoTen: this.guestForm.hoTen,
            dienThoai: this.guestForm.dienThoai,
          };

          const customAttributes = {
            ...this.contact.custom_attributes,
            crm_contact: updatedCrmInfo,
          };

          await this.$store.dispatch('contacts/update', {
            id: this.contact.id,
            custom_attributes: customAttributes,
          });

          this.onClose();
        } else {
          this.showAlert('Cập nhật thất bại. Vui lòng thử lại.');
        }
      } catch (error) {
        this.showAlert('Lỗi kết nối CRM khi cập nhật');
      } finally {
        this.isUpdating = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.modal-container {
  min-width: 620px;
}

select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1rem;
}
</style>
